import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "../components/PageHeader";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { DateField } from "../components/ui/DateField";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Plus,
  Trash2,
  Paperclip,
  Check,
  X,
  Eye,
  Receipt,
  Clock,
  CheckCircle2,
  IndianRupee,
  RefreshCw,
  Undo2,
  Pencil,
  MoreVertical,
  AlertTriangle,
  Info,
} from "lucide-react";
import { format, startOfMonth } from "date-fns";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  addExpenseCategory,
  createExpense,
  updateExpense,
  deleteExpense,
  getExpenseCategories,
  getExpenses,
  reimburseExpense,
  retryExpensePush,
  updateExpenseStatus,
  uploadReceipt,
  withdrawExpense,
  type ExpenseReport,
  type ExpenseScope,
  type ExpenseLineItem,
} from "../api/expenses";
import { vendorsApi, type Vendor } from "../api/vendors";
import { clientsApi, type Client } from "../api/clients";
import { getCompany } from "../api/officeandcompany";

const APPROVER_ROLES = [
  "manager", "hod", "hr", "hr head", "hr admin", "admin", "director", "founder",
];
const ELEVATED_ROLES = ["admin", "founder", "director", "hr head", "hr admin"];

const RECEIPT_THRESHOLD = 500;

// Today's date in the "yyyy-MM-dd" shape DateField expects.
const todayISO = () => format(new Date(), "yyyy-MM-dd");
// First day of the current month — the default start of the date filter.
const monthStartISO = () => format(startOfMonth(new Date()), "yyyy-MM-dd");

function fmtMoney(n: number | string | null | undefined): string {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  try {
    return format(new Date(s), "dd MMM yyyy");
  } catch {
    return String(s);
  }
}

// Pull a human-readable string out of an axios error. FastAPI 422s return
// `detail` as an ARRAY of {loc,msg,...} objects (other errors may return an
// object) — never hand those straight to a toast/JSX or React throws
// "Objects are not valid as a React child" and blanks the screen.
function errMsg(e: any, fallback: string): string {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d)) {
    const parts = d
      .map((x) => (x && typeof x === "object" ? x.msg : typeof x === "string" ? x : null))
      .filter(Boolean);
    if (parts.length) return parts.join(", ");
  }
  if (d && typeof d === "object" && typeof d.msg === "string") return d.msg;
  return fallback;
}

const notifySuccess = (message: string, description?: string, id?: any) =>
  toast.success(message, { id, description, duration: 3200 });

const notifyError = (message: string, description?: string, id?: any) =>
  toast.error(message, { id, description, duration: 4200 });

const STATUS_STYLES: Record<string, string> = {
  PENDING: "bg-amber-100 text-amber-800 border-amber-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  REIMBURSED: "bg-sky-100 text-sky-800 border-sky-200",
  PAID: "bg-teal-100 text-teal-800 border-teal-200",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-200",
  WITHDRAWN: "bg-gray-100 text-gray-600 border-gray-200",
};

// An owner may edit/delete their report only before it is approved/settled.
// Mirrors EXPENSE_EDITABLE_STATUSES on the backend.
const EDITABLE_STATUSES = new Set(["PENDING", "REJECTED", "WITHDRAWN"]);

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant="outline"
      className={`${STATUS_STYLES[status] || ""} font-semibold`}
    >
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </Badge>
  );
}

function SkBadge({ report }: { report: ExpenseReport }) {
  if (!["APPROVED", "REIMBURSED", "PAID"].includes(report.status)) return null;
  const s = report.sk_push_status;
  if (s === "SUCCESS")
    return <span className="text-xs text-emerald-700">✓ Synced to Khata</span>;
  if (s === "FAILED")
    return <span className="text-xs text-rose-700" title={report.sk_push_error || ""}>Sync failed</span>;
  return <span className="text-xs text-gray-400">Not synced</span>;
}

/* ------------------------------------------------------------------ */
/* New report dialog                                                  */
/* ------------------------------------------------------------------ */

// GST rate options (mirror StartupKhata's expense form).
const GST_RATES = [0, 0.1, 0.25, 1, 3, 5, 12, 18, 28, 40];
const VENDOR_NONE = "__none__";
// Sentinel for the "+ Add new…" item in the Nature of Expense dropdown.
const CAT_ADD = "__add__";

interface LineDraft {
  category: string;
  amount: string;          // base when Tax Details is off (== line total)
  taxable_amount: string;  // base when Tax Details is on
  tax_rate: string;        // GST %
  cess_percent: string;
  description: string;
  receipt_url: string;
  receipt_filename: string;
}

const blankLine = (): LineDraft => ({
  category: "",
  amount: "",
  taxable_amount: "",
  tax_rate: "0",
  cess_percent: "",
  description: "",
  receipt_url: "",
  receipt_filename: "",
});

// Reverse of the submit mapping — rehydrate a saved line item into an editable
// draft so the dialog can pre-fill it when editing.
const lineFromItem = (it: ExpenseLineItem): LineDraft => ({
  category: it.category || "",
  amount: it.amount != null ? String(it.amount) : "",
  taxable_amount: it.taxable_amount != null ? String(it.taxable_amount) : "",
  tax_rate: it.tax_rate != null ? String(it.tax_rate) : "0",
  cess_percent: it.cess_percent != null ? String(it.cess_percent) : "",
  description: it.description || "",
  receipt_url: it.receipt_url || "",
  receipt_filename: it.receipt_filename || "",
});

interface LineTax {
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
  cess_amount: number;
  total: number;
}

// Frontend mirror of utils.expense_service.compute_line_tax (the backend is the
// source of truth; this is for live UI only).
function lineTax(
  line: LineDraft,
  opts: { taxDetails: boolean; rcm: boolean; cess: boolean; sameState: boolean }
): LineTax {
  const taxable = opts.taxDetails
    ? parseFloat(line.taxable_amount) || 0
    : parseFloat(line.amount) || 0;
  const rate = opts.taxDetails ? parseFloat(line.tax_rate) || 0 : 0;
  const cessPct =
    opts.taxDetails && opts.cess && !opts.rcm ? parseFloat(line.cess_percent) || 0 : 0;
  const tax = (taxable * rate) / 100;
  let cgst = 0,
    sgst = 0,
    igst = 0;
  if (opts.sameState) {
    cgst = tax / 2;
    sgst = tax / 2;
  } else {
    igst = tax;
  }
  const cess_amount = (taxable * cessPct) / 100;
  const total = opts.rcm ? taxable : taxable + cgst + sgst + igst + cess_amount;
  return { taxable, cgst, sgst, igst, cess_amount, total };
}

function NewReportDialog({
  open,
  onOpenChange,
  categories,
  onCategoriesChange,
  onCreated,
  editing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  categories: string[];
  onCategoriesChange: (next: string[]) => void;
  onCreated: () => void;
  editing?: ExpenseReport | null;
}) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [supplierInvoiceNo, setSupplierInvoiceNo] = useState("");
  const [expenseDate, setExpenseDate] = useState(todayISO());
  const [vendorID, setVendorID] = useState("");
  const [clientID, setClientID] = useState("");
  const [taxDetails, setTaxDetails] = useState(false);
  const [rcm, setRcm] = useState(false);
  const [cess, setCess] = useState(false);
  const [alreadyPaid, setAlreadyPaid] = useState(false);
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [submitting, setSubmitting] = useState(false);

  // Inline "+ Add new…" Nature of Expense, scoped to the line that triggered it.
  const [addingForLine, setAddingForLine] = useState<number | null>(null);
  const [newCat, setNewCat] = useState("");
  const [savingCat, setSavingCat] = useState(false);

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [companyState, setCompanyState] = useState("");

  // Load the party lists + the org's own state (for the GST split) on open.
  useEffect(() => {
    if (!open) return;
    vendorsApi
      .list({ page_size: 200 })
      .then((r) => setVendors(r.data?.items || []))
      .catch(() => setVendors([]));
    clientsApi
      .list({ page_size: 200 })
      .then((r) => setClients(r.data?.items || []))
      .catch(() => setClients([]));
    getCompany()
      .then((d) => setCompanyState(d?.companies?.[0]?.state || ""))
      .catch(() => setCompanyState(""));
  }, [open]);

  // Prefill the form on open: from the report being edited, or a clean slate
  // (today's date) for a new report. Owns the date so it never goes stale.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title || "");
      setNote(editing.note || "");
      setSupplierInvoiceNo(editing.supplierInvoiceNo || "");
      setExpenseDate(editing.expenseVoucherDate || todayISO());
      setVendorID(editing.vendorID || "");
      setClientID(editing.clientID || "");
      setTaxDetails(!!editing.tax_details);
      setRcm(!!editing.rcm);
      setCess(!!editing.cess);
      setAlreadyPaid(!!editing.already_paid);
      setLines(editing.items?.length ? editing.items.map(lineFromItem) : [blankLine()]);
    } else {
      setTitle("");
      setNote("");
      setSupplierInvoiceNo("");
      setExpenseDate(todayISO());
      setVendorID("");
      setClientID("");
      setTaxDetails(false);
      setRcm(false);
      setCess(false);
      setAlreadyPaid(false);
      setLines([blankLine()]);
    }
  }, [open, editing]);

  const selectedVendor = useMemo(
    () => vendors.find((v) => v.id === vendorID),
    [vendors, vendorID]
  );
  const vendorState = selectedVendor?.state || "";
  // Missing either side ⇒ intra-state (CGST+SGST), matching the backend.
  const sameState = useMemo(() => {
    const norm = (s?: string | null) => String(s || "").trim().toLowerCase();
    if (!companyState || !vendorState) return true;
    return norm(companyState) === norm(vendorState);
  }, [companyState, vendorState]);

  const reset = () => {
    setTitle("");
    setNote("");
    setSupplierInvoiceNo("");
    setExpenseDate(todayISO());
    setVendorID("");
    setClientID("");
    setTaxDetails(false);
    setRcm(false);
    setCess(false);
    setAlreadyPaid(false);
    setLines([blankLine()]);
    setAddingForLine(null);
    setNewCat("");
  };

  // Add an org-wide Nature of Expense, then select it on the triggering line.
  const saveCat = async (i: number) => {
    const name = newCat.trim();
    if (!name) return;
    setSavingCat(true);
    const toastId = toast.loading("Adding nature of expense...");
    try {
      const next = await addExpenseCategory(name);
      onCategoriesChange(next);
      updateLine(i, { category: name });
      setAddingForLine(null);
      setNewCat("");
      notifySuccess("Nature of expense added", `${name} is selected on this line.`, toastId);
    } catch (e: any) {
      notifyError("Could not add nature of expense", errMsg(e, "Could not add"), toastId);
    } finally {
      setSavingCat(false);
    }
  };

  const updateLine = (i: number, patch: Partial<LineDraft>) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((prev) => [...prev, blankLine()]);
  const removeLine = (i: number) =>
    setLines((prev) => (prev.length === 1 ? prev : prev.filter((_, idx) => idx !== i)));

  // RCM forces Tax Details on + Cess off; Cess only valid with Tax on and no RCM.
  const onToggleTax = (v: boolean) => {
    setTaxDetails(v);
    if (!v) {
      setRcm(false);
      setCess(false);
    }
  };
  const onToggleRcm = (v: boolean) => {
    setRcm(v);
    if (v) {
      setTaxDetails(true);
      setCess(false);
    }
  };

  const handleReceipt = async (i: number, file?: File) => {
    if (!file) return;
    const toastId = toast.loading("Uploading receipt...");
    try {
      const { receipt_url, receipt_filename } = await uploadReceipt(file);
      updateLine(i, { receipt_url, receipt_filename });
      notifySuccess("Receipt attached", receipt_filename || file.name, toastId);
    } catch {
      notifyError("Receipt upload failed", "Try again or choose another file.", toastId);
    }
  };

  const computed = useMemo(
    () => lines.map((l) => lineTax(l, { taxDetails, rcm, cess, sameState })),
    [lines, taxDetails, rcm, cess, sameState]
  );
  const total = useMemo(() => computed.reduce((s, c) => s + c.total, 0), [computed]);
  // The ₹500 receipt rule is per line: a line needs its OWN receipt only when
  // that line's total exceeds the threshold. The report total never triggers it.
  const lineNeedsReceipt = (i: number) => computed[i].total > RECEIPT_THRESHOLD;
  const missingReceipt = lines.some((l, i) => lineNeedsReceipt(i) && !l.receipt_url);
  const lineBase = (l: LineDraft) =>
    taxDetails ? parseFloat(l.taxable_amount) || 0 : parseFloat(l.amount) || 0;
  const linesValid = lines.every((l) => l.category && lineBase(l) > 0);
  const canSubmit =
    !!title.trim() &&
    lines.length > 0 &&
    linesValid &&
    !missingReceipt &&
    !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    const toastId = toast.loading(editing ? "Saving expense..." : "Submitting expense...");
    try {
      const payload = {
        title: title.trim(),
        note: note.trim() || undefined,
        supplierName: selectedVendor?.clientName || undefined,
        supplierInvoiceNo: supplierInvoiceNo.trim() || undefined,
        expenseVoucherDate: expenseDate || null,
        vendorID: vendorID || null,
        clientID: clientID || null,
        tax_details: taxDetails,
        rcm,
        cess,
        already_paid: alreadyPaid,
        items: lines.map((l, i) => ({
          category: l.category,
          amount: computed[i].total,
          taxable_amount: lineBase(l),
          tax_rate: taxDetails ? parseFloat(l.tax_rate) || 0 : 0,
          cess_percent: taxDetails && cess && !rcm ? parseFloat(l.cess_percent) || 0 : 0,
          spend_date: expenseDate || null,
          description: l.description || null,
          receipt_url: l.receipt_url || null,
          receipt_filename: l.receipt_filename || null,
        })),
      };
      if (editing) {
        await updateExpense(editing.id, payload);
        notifySuccess("Expense updated", "The report is back in the approval flow.", toastId);
      } else {
        await createExpense(payload);
        notifySuccess("Expense submitted", "Your approver can review it now.", toastId);
      }
      reset();
      onOpenChange(false);
      onCreated();
    } catch (e: any) {
      notifyError(
        editing ? "Could not update expense" : "Could not submit expense",
        errMsg(e, editing ? "Could not update expense" : "Could not submit expense"),
        toastId
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto p-5 sm:p-6 gap-3">
        <DialogHeader className="space-y-1">
          <DialogTitle>{editing ? "Edit Expense" : "New Expense"}</DialogTitle>
          <DialogDescription className="text-[13px]">
            Add one or more expense lines. A receipt is required for any single
            line over {fmtMoney(RECEIPT_THRESHOLD)}.
          </DialogDescription>
        </DialogHeader>

        <motion.div
          className="grid gap-2.5"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: [0.33, 1, 0.68, 1] }}
        >
          <div
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem]"
            data-testid="expense-title-date-row"
          >
            <div className="grid gap-1.5" data-expense-field="title">
              <Label htmlFor="expense-title">Title</Label>
              <Input
                id="expense-title"
                placeholder="e.g. Client trip — Mumbai"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5" data-expense-field="date">
              <Label>Expense Date</Label>
              <DateField
                value={expenseDate}
                onChange={setExpenseDate}
                placeholder="Select date"
              />
            </div>
          </div>

          <div
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_13rem]"
            data-testid="expense-party-row"
          >
            <div className="grid gap-1.5" data-expense-field="client">
              <Label>Client (optional)</Label>
              <Select
                value={clientID || VENDOR_NONE}
                onValueChange={(v) => setClientID(v === VENDOR_NONE ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value={VENDOR_NONE}>— None —</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.clientName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5" data-expense-field="vendor">
              <Label>Vendor (optional)</Label>
              <Select
                value={vendorID || VENDOR_NONE}
                onValueChange={(v) => setVendorID(v === VENDOR_NONE ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  <SelectItem value={VENDOR_NONE}>— None —</SelectItem>
                  {vendors.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.clientName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5" data-expense-field="invoice">
              <Label htmlFor="expense-invoice-no">Invoice No.</Label>
              <Input
                id="expense-invoice-no"
                value={supplierInvoiceNo}
                onChange={(e) => setSupplierInvoiceNo(e.target.value)}
              />
            </div>
          </div>

          <div
            className="flex flex-wrap items-center gap-2"
            data-testid="expense-flags-line-header-row"
          >
            <Label
              data-testid="expense-line-items-heading"
              data-control-section="line-items-heading"
            >
              Line items
            </Label>
            <div
              className="flex flex-wrap items-center gap-1.5"
              data-testid="expense-flags-row"
              data-compact-flags="true"
              data-flag-size="xs"
              data-control-section="flags"
            >
              <label
                className="inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold"
                data-expense-field="tax-details"
              >
                <Switch
                  className="-mx-1 scale-[0.72]"
                  checked={taxDetails}
                  onCheckedChange={onToggleTax}
                />{" "}
                Tax Details
              </label>
              <label
                className="inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold"
                data-expense-field="rcm"
              >
                <Switch
                  className="-mx-1 scale-[0.72]"
                  checked={rcm}
                  onCheckedChange={onToggleRcm}
                />{" "}
                RCM
              </label>
              <label
                className={`inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold ${
                  !taxDetails || rcm ? "opacity-50" : ""
                }`}
                data-expense-field="cess"
              >
                <Switch
                  className="-mx-1 scale-[0.72]"
                  checked={cess}
                  onCheckedChange={setCess}
                  disabled={!taxDetails || rcm}
                />{" "}
                Cess
              </label>
              <div
                className="inline-flex h-7 items-center gap-1 rounded-full bg-gray-50 px-2 text-[11px] font-semibold"
                data-expense-field="already-paid"
              >
                <Switch
                  id="expense-already-paid"
                  className="-mx-1 scale-[0.72]"
                  checked={alreadyPaid}
                  onCheckedChange={setAlreadyPaid}
                />
                <Label htmlFor="expense-already-paid" className="cursor-pointer text-[11px]">
                  Already paid
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      aria-label="Already paid info"
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition hover:border-gray-300 hover:text-gray-900"
                    >
                      <Info size={10} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-80 text-sm leading-6">
                    I've already paid this, so no reimbursement is owed. It still
                    needs approval and settles as <b>Paid</b>.
                  </PopoverContent>
                </Popover>
              </div>
              {taxDetails && (
                <span className="text-xs text-gray-500">
                  {vendorID
                    ? sameState
                      ? "Intra-state — CGST + SGST"
                      : "Inter-state — IGST"
                    : "Pick a vendor to set place of supply (defaulting to intra-state)"}
                </span>
              )}
            </div>

            <button
              className="ml-auto inline-flex h-7 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 text-[11px] font-semibold text-emerald-950 transition hover:bg-emerald-100"
              type="button"
              data-size="xs"
              data-control-section="add-line"
              onClick={addLine}
            >
              <Plus size={12} /> Add line
            </button>
          </div>

          <div className="grid gap-3">
            <div
              className="overflow-hidden rounded-lg border border-gray-200 bg-white"
              data-testid="expense-lines-table"
              data-single-line-items-box="true"
            >
              <div className="hidden border-b border-gray-100 bg-gray-50/60 px-3 py-1.5 text-xs font-medium text-gray-500 lg:grid lg:grid-cols-[2.25rem_minmax(180px,1fr)_minmax(220px,1.25fr)_minmax(120px,0.55fr)_2.5rem_2.5rem] lg:gap-2">
                <span>#</span>
                <span>Nature of Expense</span>
                <span>Description</span>
                <span>Amount (₹)</span>
                <span>Receipt</span>
                <span />
              </div>
              <AnimatePresence initial={false}>
                {lines.map((line, i) => (
                  <motion.div
                    key={i}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.16, ease: [0.33, 1, 0.68, 1] }}
                    className={`grid gap-2 px-3 py-2 ${i === 0 ? "" : "border-t border-gray-100"}`}
                  >
                  <div
                    className="grid gap-2 lg:grid-cols-[2.25rem_minmax(180px,1fr)_minmax(220px,1.25fr)_minmax(120px,0.55fr)_2.5rem_2.5rem] lg:items-center"
                    data-testid={`expense-line-row-${i}`}
                  >
                    <div
                      className="flex h-9 items-center text-xs font-bold text-gray-500"
                      data-line-field="serial"
                    >
                      {i + 1}
                    </div>
                    <div data-line-field="nature">
                      <span className="sr-only">Nature of Expense</span>
                      <Select
                        value={line.category}
                        onValueChange={(v) => {
                          if (v === CAT_ADD) {
                            setAddingForLine(i);
                            setNewCat("");
                          } else {
                            updateLine(i, { category: v });
                          }
                        }}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                          <SelectItem value={CAT_ADD} className="text-emerald-700 font-medium">
                            + Add new…
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div data-line-field="description">
                      <span className="sr-only">Description</span>
                      <Input
                        className="h-9"
                        value={line.description}
                        onChange={(e) => updateLine(i, { description: e.target.value })}
                      />
                    </div>
                    <div data-line-field="amount">
                      <span className="sr-only">
                        {taxDetails ? "Taxable amount" : "Amount"}
                      </span>
                      <Input
                        className="h-9"
                        type="number"
                        min="0"
                        value={taxDetails ? line.taxable_amount : line.amount}
                        onChange={(e) =>
                          updateLine(
                            i,
                            taxDetails
                              ? { taxable_amount: e.target.value }
                              : { amount: e.target.value }
                          )
                        }
                      />
                    </div>
                    <div data-line-field="receipt">
                      <span className="sr-only">Receipt</span>
                      <label
                        className="relative inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                        title={line.receipt_filename ? "Replace receipt" : "Attach receipt"}
                      >
                        <Paperclip size={15} />
                        <span className="sr-only">
                          {line.receipt_filename ? "Replace receipt" : "Attach receipt"}
                        </span>
                        {line.receipt_filename && (
                          <span className="absolute -right-0.5 -top-0.5 grid h-4 w-4 place-items-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
                            1
                          </span>
                        )}
                        <input
                          type="file"
                          className="hidden"
                          onChange={(e) => handleReceipt(i, e.target.files?.[0])}
                        />
                      </label>
                    </div>
                    <button
                      type="button"
                      className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 transition hover:border-gray-300 hover:bg-gray-50"
                      onClick={() => removeLine(i)}
                      title="Remove line"
                    >
                      <Trash2 size={14} />
                      <span className="sr-only">Remove line</span>
                    </button>
                  </div>

                  {taxDetails && (
                    <div className="grid gap-2 rounded-md bg-gray-50 px-2 py-1.5 sm:grid-cols-[6.5rem_6.5rem_minmax(0,1fr)] sm:items-end lg:ml-[2.75rem]">
                      <div className="grid gap-1">
                      <span className="text-[11px] text-gray-500">GST Rate</span>
                      <Select
                        value={line.tax_rate}
                        onValueChange={(v) => updateLine(i, { tax_rate: v })}
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {GST_RATES.map((r) => (
                            <SelectItem key={r} value={String(r)}>
                              {r}%
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                      {cess && !rcm && (
                        <div className="grid gap-1">
                          <span className="text-[11px] text-gray-500">Cess %</span>
                          <Input
                            className="h-8"
                            type="number"
                            min="0"
                            max="999"
                            value={line.cess_percent}
                            onChange={(e) => updateLine(i, { cess_percent: e.target.value })}
                          />
                        </div>
                      )}
                      {taxDetails && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 sm:justify-end">
                          {sameState ? (
                            <>
                              <span>CGST <b>{fmtMoney(computed[i].cgst)}</b></span>
                              <span>SGST <b>{fmtMoney(computed[i].sgst)}</b></span>
                            </>
                          ) : (
                            <span>IGST <b>{fmtMoney(computed[i].igst)}</b></span>
                          )}
                          {cess && !rcm && (
                            <span>Cess <b>{fmtMoney(computed[i].cess_amount)}</b></span>
                          )}
                          {rcm && (
                            <span className="text-amber-700">
                              Reverse charge — tax not added to payable
                            </span>
                          )}
                          <span>
                            Line total <b>{fmtMoney(computed[i].total)}</b>
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                {addingForLine === i && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-end gap-2 rounded-md bg-emerald-50/60 p-2 lg:ml-[2.75rem]"
                    >
                    <div className="grid gap-1 flex-1">
                      <span className="text-[11px] text-gray-500">New Nature of Expense</span>
                      <Input
                        className="h-9"
                        autoFocus
                        placeholder="e.g. Cloud Hosting"
                        value={newCat}
                        onChange={(e) => setNewCat(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            saveCat(i);
                          }
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      className="gt-btn is-primary is-sm"
                      disabled={savingCat || !newCat.trim()}
                      onClick={() => saveCat(i)}
                    >
                      {savingCat ? "Saving…" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="gt-btn is-ghost is-sm"
                      onClick={() => {
                        setAddingForLine(null);
                        setNewCat("");
                      }}
                    >
                      Cancel
                    </button>
                    </motion.div>
                )}

                  {line.receipt_filename && (
                    <span className="flex items-center gap-1 text-xs text-emerald-700 lg:ml-[2.75rem]">
                      <Check size={12} /> {line.receipt_filename}
                    </span>
                  )}
                  {lineNeedsReceipt(i) && !line.receipt_url && (
                    <span className="flex items-center gap-1 text-xs text-amber-700 lg:ml-[2.75rem]">
                      <AlertTriangle size={12} /> Receipt required — over{" "}
                      {fmtMoney(RECEIPT_THRESHOLD)}
                    </span>
                  )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          <div
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_14rem] md:items-end"
            data-testid="expense-note-total-row"
          >
            <div className="grid gap-1.5" data-summary-field="note">
              <Label>Note (optional)</Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="min-h-[64px]"
              />
            </div>
            <div
              className="flex min-h-[64px] items-center justify-between rounded-lg bg-gray-50 px-4 py-3 md:flex-col md:items-start md:justify-center"
              data-summary-field="total"
            >
              <span className="text-sm text-gray-600">Total</span>
              <span className="text-xl font-bold">{fmtMoney(total)}</span>
            </div>
          </div>

          {missingReceipt && (
            <div className="flex items-center gap-2 text-sm text-amber-700">
              <AlertTriangle size={16} />
              Attach a receipt to each line over {fmtMoney(RECEIPT_THRESHOLD)}.
            </div>
          )}
        </motion.div>

        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </button>
          <button className="gt-btn is-primary" disabled={!canSubmit} onClick={submit}>
            {submitting
              ? editing
                ? "Saving…"
                : "Submitting…"
              : editing
                ? "Save changes"
                : "Submit"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Decision (approve/reject) dialog                                   */
/* ------------------------------------------------------------------ */

function DecisionDialog({
  state,
  onClose,
  onDone,
}: {
  state: { report: ExpenseReport; action: "approved" | "rejected" } | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => setNote(""), [state]);

  if (!state) return null;
  const { report, action } = state;

  const confirm = async () => {
    setBusy(true);
    const toastId = toast.loading(action === "approved" ? "Approving expense..." : "Rejecting expense...");
    try {
      await updateExpenseStatus(report.id, action, note.trim() || undefined);
      notifySuccess(`Expense ${action}`, report.title, toastId);
      onClose();
      onDone();
    } catch (e: any) {
      notifyError("Action failed", errMsg(e, "Action failed"), toastId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {action === "approved" ? "Approve" : "Reject"} — {report.title}
          </DialogTitle>
          <DialogDescription>
            {report.name} · {fmtMoney(report.total_amount)} · {report.items.length} item(s)
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label>Note (optional)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
        </div>
        <DialogFooter>
          <button className="gt-btn is-ghost" onClick={onClose}>Cancel</button>
          <button
            className={`gt-btn ${action === "approved" ? "is-primary" : "is-danger"}`}
            disabled={busy}
            onClick={confirm}
          >
            {busy ? "Saving…" : action === "approved" ? "Approve" : "Reject"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Detail dialog                                                      */
/* ------------------------------------------------------------------ */

function DetailDialog({ report, onClose }: { report: ExpenseReport | null; onClose: () => void }) {
  if (!report) return null;
  return (
    <Dialog open={!!report} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {report.title} <StatusBadge status={report.status} />
          </DialogTitle>
          <DialogDescription>
            {report.name} · {fmtDate(report.expenseVoucherDate)}
            {report.supplierName ? ` · ${report.supplierName}` : ""}
            {report.supplierInvoiceNo ? ` · Inv ${report.supplierInvoiceNo}` : ""}
          </DialogDescription>
        </DialogHeader>
        {report.tax_details && (
          <div className="flex flex-wrap gap-2 text-xs">
            <Badge variant="outline">Tax details</Badge>
            {report.rcm && <Badge variant="outline">RCM</Badge>}
            {report.cess && <Badge variant="outline">Cess</Badge>}
          </div>
        )}
        <div className="grid gap-3">
          {report.items.map((it) => (
            <div key={it.id} className="flex items-start justify-between border-b border-gray-100 pb-2">
              <div>
                <div className="font-medium">{it.category}</div>
                <div className="text-xs text-gray-500">
                  {fmtDate(it.spend_date)} {it.description ? `· ${it.description}` : ""}
                </div>
                {report.tax_details && (
                  <div className="text-xs text-gray-500 mt-0.5">
                    Taxable {fmtMoney(it.taxable_amount)} @ {Number(it.tax_rate || 0)}%
                    {Number(it.igst || 0) > 0
                      ? ` · IGST ${fmtMoney(it.igst)}`
                      : ` · CGST ${fmtMoney(it.cgst)} · SGST ${fmtMoney(it.sgst)}`}
                    {Number(it.cess_amount || 0) > 0 ? ` · Cess ${fmtMoney(it.cess_amount)}` : ""}
                  </div>
                )}
                {it.receipt_url && (
                  <a
                    href={it.receipt_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-sky-700 inline-flex items-center gap-1 mt-1"
                  >
                    <Paperclip size={12} /> {it.receipt_filename || "Receipt"}
                  </a>
                )}
              </div>
              <div className="font-semibold">{fmtMoney(it.amount)}</div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <span className="font-semibold">Total</span>
            <span className="text-lg font-bold">{fmtMoney(report.total_amount)}</span>
          </div>
          {report.decision_note && (
            <div className="text-sm text-gray-600">
              <span className="font-medium">Approver note:</span> {report.decision_note}
            </div>
          )}
          <SkBadge report={report} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                          */
/* ------------------------------------------------------------------ */

export function Expenses() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const canApprove = APPROVER_ROLES.includes(role);
  const isElevated = ELEVATED_ROLES.includes(role);

  const [tab, setTab] = useState<ExpenseScope>("mine");
  // Default the range to "this month so far": 1st of the current month → today.
  const [fromDate, setFromDate] = useState(monthStartISO);
  const [toDate, setToDate] = useState(todayISO);
  const [reports, setReports] = useState<ExpenseReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState<string[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ExpenseReport | null>(null);
  const [deleting, setDeleting] = useState<ExpenseReport | null>(null);
  const [detail, setDetail] = useState<ExpenseReport | null>(null);
  const [decision, setDecision] = useState<{ report: ExpenseReport; action: "approved" | "rejected" } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { expenses } = await getExpenses(tab);
      setReports(expenses);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getExpenseCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  // Land on the first tab available for this role — All for admins, Pending
  // Approvals for approvers, else My Expenses. Runs once the role resolves
  // (user hydrates async) and never overrides a later manual tab switch.
  const didInitTab = useRef(false);
  useEffect(() => {
    if (didInitTab.current || !user) return;
    didInitTab.current = true;
    if (isElevated) setTab("all");
    else if (canApprove) setTab("pending");
  }, [user, isElevated, canApprove]);

  // Date-range filter on the submitted date. yyyy-MM-dd compares lexicographically
  // = chronologically, so plain string comparison is safe. Undated rows drop out
  // once any bound is set since they can't be placed in a range.
  const dateFilterActive = Boolean(fromDate || toDate);
  const filtered = useMemo(() => {
    if (!dateFilterActive) return reports;
    return reports.filter((r) => {
      if (!r.submitted_on) return false;
      let d: string;
      try {
        d = format(new Date(r.submitted_on), "yyyy-MM-dd");
      } catch {
        return false;
      }
      if (fromDate && d < fromDate) return false;
      if (toDate && d > toDate) return false;
      return true;
    });
  }, [reports, fromDate, toDate, dateFilterActive]);

  const stats = useMemo(() => {
    const pending = filtered.filter((r) => r.status === "PENDING");
    const approved = filtered.filter((r) => r.status === "APPROVED");
    const reimbursed = filtered.filter((r) => r.status === "REIMBURSED");
    const sum = (rs: ExpenseReport[]) => rs.reduce((s, r) => s + Number(r.total_amount || 0), 0);
    return {
      pendingCount: pending.length,
      pendingAmount: sum(pending),
      approvedAmount: sum(approved),
      reimbursedAmount: sum(reimbursed),
    };
  }, [filtered]);

  const doWithdraw = async (r: ExpenseReport) => {
    const toastId = toast.loading("Withdrawing expense...");
    try {
      await withdrawExpense(r.id);
      notifySuccess("Expense withdrawn", r.title, toastId);
      load();
    } catch (e: any) {
      notifyError("Could not withdraw", errMsg(e, "Could not withdraw"), toastId);
    }
  };

  const doReimburse = async (r: ExpenseReport) => {
    const toastId = toast.loading("Marking reimbursement...");
    try {
      await reimburseExpense(r.id);
      notifySuccess("Marked as reimbursed", r.title, toastId);
      load();
    } catch (e: any) {
      notifyError("Could not mark reimbursed", errMsg(e, "Could not mark reimbursed"), toastId);
    }
  };

  const doRetry = async (r: ExpenseReport) => {
    const toastId = toast.loading("Retrying StartupKhata push...");
    try {
      const res = await retryExpensePush(r.id);
      notifySuccess("StartupKhata push retried", res?.status || "Done", toastId);
      load();
    } catch (e: any) {
      notifyError("Retry failed", errMsg(e, "Retry failed"), toastId);
    }
  };

  const doDelete = async (r: ExpenseReport) => {
    const toastId = toast.loading("Deleting expense...");
    try {
      await deleteExpense(r.id);
      notifySuccess("Expense deleted", r.title, toastId);
      setDeleting(null);
      load();
    } catch (e: any) {
      notifyError("Could not delete", errMsg(e, "Could not delete"), toastId);
    }
  };

  const tabs: { key: ExpenseScope; label: string }[] = [
    ...(isElevated ? [{ key: "all" as ExpenseScope, label: "All" }] : []),
    ...(canApprove ? [{ key: "pending" as ExpenseScope, label: "Pending Approvals" }] : []),
    { key: "mine", label: "My Expenses" },
  ];

  return (
    <div className="gt-themed p-6 space-y-6">
      <PageHeader
        title={<>Expense <em>Management</em></>}
        subtitle={<>Track, approve and reimburse <em>employee</em> expenses.</>}
        actions={
          <button className="gt-btn is-primary" onClick={() => setCreateOpen(true)}>
            <Plus size={16} /> New Expense
          </button>
        }
      />

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="gt-stat">
          <div className="gt-stat-label"><Clock size={14} /> Pending</div>
          <div className="gt-stat-value">{stats.pendingCount}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><IndianRupee size={14} /> Pending value</div>
          <div className="gt-stat-value">{fmtMoney(stats.pendingAmount)}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><CheckCircle2 size={14} /> Approved</div>
          <div className="gt-stat-value">{fmtMoney(stats.approvedAmount)}</div>
        </div>
        <div className="gt-stat">
          <div className="gt-stat-label"><Receipt size={14} /> Reimbursed</div>
          <div className="gt-stat-value">{fmtMoney(stats.reimbursedAmount)}</div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as ExpenseScope)}>
        <TabsList>
          {tabs.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base">
              {tabs.find((t) => t.key === tab)?.label}
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Inline width: this build purges unused Tailwind width classes
                  (only w-36/w-full exist), so size the wrapper via style. */}
              <div style={{ width: "12rem", flexShrink: 0 }}>
                <DateField
                  value={fromDate}
                  onChange={setFromDate}
                  placeholder="Start date"
                  size="sm"
                  className="w-full"
                />
              </div>
              <span className="text-gray-400 text-sm">–</span>
              <div style={{ width: "12rem", flexShrink: 0 }}>
                <DateField
                  value={toDate}
                  onChange={setToDate}
                  placeholder="End date"
                  size="sm"
                  className="w-full"
                />
              </div>
              {dateFilterActive && (
                <button
                  type="button"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                  }}
                  className="gt-btn is-ghost is-sm"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-gray-400">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="py-10 text-center text-gray-400">
              {dateFilterActive
                ? "No expenses in this date range."
                : "No expenses here yet."}
            </div>
          ) : (
            <div className="overflow-auto max-h-[70vh]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="sticky top-0 z-10 bg-white py-2 pr-3">Expense</th>
                    {tab !== "mine" && <th className="sticky top-0 z-10 bg-white py-2 pr-3">Employee</th>}
                    <th className="sticky top-0 z-10 bg-white py-2 pr-3">Items</th>
                    <th className="sticky top-0 z-10 bg-white py-2 pr-3">Total</th>
                    <th className="sticky top-0 z-10 bg-white py-2 pr-3">Status</th>
                    <th className="sticky top-0 z-10 bg-white py-2 pr-3">Submitted</th>
                    <th className="sticky top-0 z-10 bg-white py-2 pr-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 pr-3 font-medium">{r.title}</td>
                      {tab !== "mine" && <td className="py-3 pr-3">{r.name || "—"}</td>}
                      <td className="py-3 pr-3">{r.items.length}</td>
                      <td className="py-3 pr-3 font-semibold">{fmtMoney(r.total_amount)}</td>
                      <td className="py-3 pr-3">
                        <div className="flex flex-col gap-1">
                          <StatusBadge status={r.status} />
                          <SkBadge report={r} />
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-gray-500">{fmtDate(r.submitted_on)}</td>
                      <td className="py-3 pr-3">
                        <div className="flex items-center justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="gt-btn is-ghost is-sm"
                                aria-label="Actions"
                                title="Actions"
                              >
                                <MoreVertical size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44 bg-white">
                              <DropdownMenuItem className="cursor-pointer" onClick={() => setDetail(r)}>
                                <Eye size={14} className="mr-2" /> View
                              </DropdownMenuItem>

                              {tab !== "mine" && r.status === "PENDING" && (
                                <>
                                  <DropdownMenuItem
                                    className="cursor-pointer text-emerald-700 focus:text-emerald-700"
                                    onClick={() => setDecision({ report: r, action: "approved" })}
                                  >
                                    <Check size={14} className="mr-2" /> Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer text-rose-600 focus:text-rose-600"
                                    onClick={() => setDecision({ report: r, action: "rejected" })}
                                  >
                                    <X size={14} className="mr-2" /> Reject
                                  </DropdownMenuItem>
                                </>
                              )}

                              {tab === "mine" && r.status === "PENDING" && (
                                <DropdownMenuItem className="cursor-pointer" onClick={() => doWithdraw(r)}>
                                  <Undo2 size={14} className="mr-2" /> Withdraw
                                </DropdownMenuItem>
                              )}

                              {tab === "mine" && EDITABLE_STATUSES.has(r.status) && (
                                <>
                                  <DropdownMenuItem className="cursor-pointer" onClick={() => setEditingReport(r)}>
                                    <Pencil size={14} className="mr-2" /> Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer text-red-600 focus:text-red-600"
                                    onClick={() => setDeleting(r)}
                                  >
                                    <Trash2 size={14} className="mr-2" /> Delete
                                  </DropdownMenuItem>
                                </>
                              )}

                              {isElevated && r.status === "APPROVED" && (
                                <DropdownMenuItem className="cursor-pointer" onClick={() => doReimburse(r)}>
                                  <Receipt size={14} className="mr-2" /> Mark reimbursed
                                </DropdownMenuItem>
                              )}

                              {isElevated &&
                                (r.status === "APPROVED" || r.status === "REIMBURSED") &&
                                r.sk_push_status === "FAILED" && (
                                  <DropdownMenuItem className="cursor-pointer" onClick={() => doRetry(r)}>
                                    <RefreshCw size={14} className="mr-2" /> Retry sync
                                  </DropdownMenuItem>
                                )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <NewReportDialog
        open={createOpen || !!editingReport}
        onOpenChange={(v) => {
          if (!v) {
            setCreateOpen(false);
            setEditingReport(null);
          } else {
            setCreateOpen(true);
          }
        }}
        editing={editingReport}
        categories={categories}
        onCategoriesChange={setCategories}
        onCreated={load}
      />
      <DecisionDialog state={decision} onClose={() => setDecision(null)} onDone={load} />
      <DetailDialog report={detail} onClose={() => setDetail(null)} />
      <Dialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete expense?</DialogTitle>
            <DialogDescription>
              "{deleting?.title}" ({fmtMoney(deleting?.total_amount ?? 0)}) will be
              removed. This can't be undone from here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button className="gt-btn is-ghost" onClick={() => setDeleting(null)}>
              Cancel
            </button>
            <button
              className="gt-btn is-danger"
              onClick={() => deleting && doDelete(deleting)}
            >
              <Trash2 size={14} /> Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Expenses;
